/* eslint-disable */

import { getCurrencyCode } from '~/utils/localization';

/**
 * Format a number to currency. Uses localCurrency cookie for currency code or defaults to USD.
 * @param {number} x - number to format as currency. Defaults to 0.
 * @param {boolean} decimal - Whether to include decimal value in returned string. Defaults to true.
 */
export const formatCurrency = (amount = 0, decimal = true, currencyCode = getCurrencyCode()): string => {
    const currencyFractionDigits = decimal
        ? new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: currencyCode
          }).resolvedOptions().maximumFractionDigits
        : 0;

    return amount.toLocaleString(currencyCode, {
        currency: currencyCode,
        style: 'currency',
        maximumFractionDigits: currencyFractionDigits,
        minimumFractionDigits: currencyFractionDigits
    });
};

/**
 * Format number into users local currency in cents.
 * Denomination * 100.
 * @param amount - amount
 * @example - $500.00
 */
export const formatCurrencyInCents = (amount: number, decimal = true, currencyCode = getCurrencyCode()): string => {
    return formatCurrency(amount / 100, decimal, currencyCode);
};

/**
 * Format the currency with a default placeholder number if 0.
 * @param amount - amount to format
 * @param placeholder - placeholder to use
 * @returns
 */
export const formatCurrencyWithPlaceholder = (amount: number, placeholder = 1000): string => {
    if (amount < placeholder) {
        const currencyStr = formatCurrencyInCents(amount > 0 ? amount + placeholder : placeholder);
        return currencyStr.replace(/(\d)/, '0');
    }
    return formatCurrencyRoundedCents(amount);
};

/**
 * Format cent dollars as a rounded whole number.
 * @param amount - amount as cent dollars.
 * @example - 50000 returns as $500
 */
export const formatCurrencyRoundedCents = (amount: number): string => {
    const roundedCentDollars = Math.round(amount / 100);
    return formatCurrency(roundedCentDollars, false);
};

/**
 * Format currency using 3-digit short notation (e.g., $25.1k, $1.02M).
 * @param amount - amount in cents.
 * @param decimal - Whether to include decimal value in returned string. Defaults to true.
 * @param currencyCode - Currency code to use. Defaults to user's local currency.
 * @example - 2510000 returns as $2.51M
 */
export const formatCurrencyShortNotation = (
    amount: number,
    decimal = true,
    currencyCode = getCurrencyCode()
): string => {
    const roundedAmount = amount / 100; // Convert cents to dollars
    let formattedAmount: string;

    if (roundedAmount >= 1_000_000 && decimal) {
        // Format as millions
        formattedAmount = `${(roundedAmount / 1_000_000).toFixed(2)}M`;

    } else if (roundedAmount >= 1_000  && decimal) {
        // Format as thousands
        formattedAmount = `${(roundedAmount / 1_000).toFixed(1)}k`;

    } else if (roundedAmount >= 1_000_000) {
        // Format as millions
        formattedAmount = `${Math.round(roundedAmount / 1_000_000)}M`;

    } else if (roundedAmount >= 1_000) {
        // Format as thousands
        formattedAmount = `${Math.round(roundedAmount / 1_000)}k`;

    } else {
        // Format as regular currency
        formattedAmount = roundedAmount.toFixed(decimal ? 2 : 0);
    }

    return (
        new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currencyCode,
            maximumFractionDigits: decimal ? 2 : 0
        }).format(Number(formattedAmount.replace(/[kM]/, ''))) +
        (formattedAmount.includes('k')
            ? 'k'
            : formattedAmount.includes('M')
            ? 'M'
            : '')
    );
};

/**
 * Format cent dollars as a rounded whole number.
 * @param amount - amount as cent dollars.
 * @example - 50000 returns as $500
 */
export const formatCurrencyCeilCents = (amount: number): string => {
    const roundedCentDollars = Math.ceil(amount / 100);
    return formatCurrency(roundedCentDollars, false);
};

/**
 * For trading we use amounts in whole dollars but it must be rounded up to cover the price of the offer.
 */
export const ceilCurrencyInCentsToFloat = (amount?: number): number | undefined => {
    if (amount === undefined || amount === null) {
        return undefined;
    }
    return Math.ceil(amount / 100) * 100;
};
